import { useMutation, useQuery } from 'react-query';
import { httpRequest } from '../config/api';

const DEFAULT_CONNECT_UI_URL = 'https://connect.integrations.datox.ai';
const DEFAULT_NANGO_API_URL = 'https://integrations.datox.ai';

const sanitizeUrl = value => (value || '').toString().trim().replace(/\/$/, '');

const getConnectLinkFromResponse = response =>
  response?.connect_link || response?.data?.connect_link || response?.data?.data?.connect_link;

/**
 * Build a URL that always opens Nango Connect UI with a valid session token.
 * This avoids routing users to dashboard pages (e.g. /prod) when self-hosted
 * server URLs are used as connect links.
 */
export const buildConnectUiUrl = response => {
  const connectLink = getConnectLinkFromResponse(response);
  if (!connectLink) return '';

  try {
    const source = new URL(connectLink);
    const sessionToken = source.searchParams.get('session_token');
    if (!sessionToken) return connectLink;

    const configuredBase = sanitizeUrl(import.meta.env.VITE_NANGO_CONNECT_UI_URL);

    // If configured base points to the same host as the API link, it often resolves
    // to the dashboard app and redirects to /signin. Fallback to Connect UI host.
    let base = configuredBase || DEFAULT_CONNECT_UI_URL;
    try {
      if (configuredBase) {
        const configured = new URL(configuredBase);
        if (configured.origin === source.origin) {
          base = DEFAULT_CONNECT_UI_URL;
        }
      }
    } catch {
      base = DEFAULT_CONNECT_UI_URL;
    }

    const target = new URL(base);

    const configuredApiBase = sanitizeUrl(
      import.meta.env.VITE_NANGO_API_URL || import.meta.env.VITE_NANGO_SERVER_URL
    );

    let apiBase = configuredApiBase || source.origin;

    // When Connect UI is hosted on a dedicated connect.* host, API calls must
    // still hit the main Nango server host.
    try {
      const connectUrl = new URL(base);
      const connectOrigin = connectUrl.origin;
      const isConnectSubdomain = connectUrl.hostname.startsWith('connect.');
      if (source.origin === connectOrigin && isConnectSubdomain && !configuredApiBase) {
        apiBase = DEFAULT_NANGO_API_URL;
      }
    } catch {
      apiBase = configuredApiBase || DEFAULT_NANGO_API_URL;
    }

    target.searchParams.set('session_token', sessionToken);
    target.searchParams.set('apiURL', apiBase);

    return target.toString();
  } catch {
    return connectLink;
  }
};

const service = {
  createConnectSession: data => httpRequest.post('api/integrations/connect-session', data),
  createReconnectSession: data => httpRequest.post('api/integrations/reconnect-session', data),
  listConnections: params => httpRequest.get('api/integrations/connections', { params }),
  deleteConnection: params => httpRequest.delete('api/integrations/connections', { params }),
  listFiles: params => httpRequest.get('api/integrations/files', { params }),
  listSharepointSites: params => httpRequest.get('api/integrations/sharepoint/sites', { params }),
  listSharepointDrives: params => httpRequest.get('api/integrations/sharepoint/drives', { params }),
  listSharepointFolders: params =>
    httpRequest.get('api/integrations/sharepoint/folders', { params }),
  getSharepointSyncConfig: params =>
    httpRequest.get('api/integrations/sharepoint/sync-config', { params }),
  updateSharepointSyncConfig: data =>
    httpRequest.put('api/integrations/sharepoint/sync-config', data),
  syncSharepointNow: data => httpRequest.post('api/integrations/sharepoint/sync-now', data),
  importCloudFiles: ({ periodId, data }) =>
    httpRequest.post(`api/integrations/periods/${periodId}/files/import-cloud`, data),
  triggerSync: data => httpRequest.post('api/integrations/sync/trigger', data),
  getSyncStatus: params => httpRequest.get('api/integrations/sync/status', { params })
};

export const useCreateConnectSession = ({ mutationOptions = {} } = {}) => {
  return useMutation(service.createConnectSession, mutationOptions);
};

export const useCreateReconnectSession = ({ mutationOptions = {} } = {}) => {
  return useMutation(service.createReconnectSession, mutationOptions);
};

export const useIntegrationConnections = ({ params = {}, queryOptions = {} } = {}) => {
  return useQuery(
    ['INTEGRATION_CONNECTIONS', params],
    () => service.listConnections(params),
    queryOptions
  );
};

export const useDeleteIntegrationConnection = ({ mutationOptions = {} } = {}) => {
  return useMutation(service.deleteConnection, mutationOptions);
};

export const useIntegrationFiles = ({ params = {}, queryOptions = {} } = {}) => {
  return useQuery(['INTEGRATION_FILES', params], () => service.listFiles(params), queryOptions);
};

export const useSharepointSites = ({ params = {}, queryOptions = {} } = {}) => {
  return useQuery(
    ['INTEGRATION_SHAREPOINT_SITES', params],
    () => service.listSharepointSites(params),
    queryOptions
  );
};

export const useSharepointDrives = ({ params = {}, queryOptions = {} } = {}) => {
  return useQuery(
    ['INTEGRATION_SHAREPOINT_DRIVES', params],
    () => service.listSharepointDrives(params),
    queryOptions
  );
};

export const useSharepointFolders = ({ params = {}, queryOptions = {} } = {}) => {
  return useQuery(
    ['INTEGRATION_SHAREPOINT_FOLDERS', params],
    () => service.listSharepointFolders(params),
    queryOptions
  );
};

export const useSharepointSyncConfig = ({ params = {}, queryOptions = {} } = {}) => {
  return useQuery(
    ['INTEGRATION_SHAREPOINT_SYNC_CONFIG', params],
    () => service.getSharepointSyncConfig(params),
    queryOptions
  );
};

export const useUpdateSharepointSyncConfig = ({ mutationOptions = {} } = {}) => {
  return useMutation(service.updateSharepointSyncConfig, mutationOptions);
};

export const useSyncSharepointNow = ({ mutationOptions = {} } = {}) => {
  return useMutation(service.syncSharepointNow, mutationOptions);
};

export const useImportCloudFiles = ({ mutationOptions = {} } = {}) => {
  return useMutation(service.importCloudFiles, mutationOptions);
};

export const useTriggerIntegrationSync = ({ mutationOptions = {} } = {}) => {
  return useMutation(service.triggerSync, mutationOptions);
};

export const useIntegrationSyncStatus = ({ params = {}, queryOptions = {} } = {}) => {
  return useQuery(
    ['INTEGRATION_SYNC_STATUS', params],
    () => service.getSyncStatus(params),
    queryOptions
  );
};

export default service;
